import { MarkRetest, RETEST_HINT } from '../MarkRetest';
import { WorkflowSteps, WorkflowBreadcrumb } from '../WorkflowCard';
import { priorityLabel } from '../../utils/presentation';
import { assigneeLabel, formatTaskHistory, statusLabel } from '../../utils/taskPresentation';
import RiskBadge from '../RiskBadge';
import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  User as UserIcon,
  Building,
  Package,
  FileText,
  Calendar,
  MessageSquare,
  Edit3,
  UserCheck,
  Flag,
  Tag,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { tasksService, facilitiesService, assetsService } from '../../services';
import type { TaskDetailResponse } from '../../services/tasks';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  cellCode,
  getAssetOwnRisk,
  getRiskLevel,
  riskLabel,
  toFacilityBadgeKey,
} from '../../utils/riskUtils';
import { formatShortDate } from '../../utils/dateUtils';
import type { Asset } from '../../types';
import RiskMatrixModal from './RiskMatrixModal';
import './ActionDetailModal.css';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ActionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string | null;
  onUpdated?: () => void;
}

const ActionDetailModal: React.FC<ActionDetailModalProps> = ({
  isOpen,
  onClose,
  taskId,
  onUpdated
}) => {
  const navigate = useNavigate();
  const [nestedTaskId, setNestedTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<TaskDetailResponse | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Action panel states
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState('');
  const [showReassignBox, setShowReassignBox] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [linkedAsset, setLinkedAsset] = useState<Asset | null>(null);
  const [showRiskMatrixModal, setShowRiskMatrixModal] = useState(false);
  const [showPriorityRiskModal, setShowPriorityRiskModal] = useState(false);

  const { showSuccess, showError } = useToast();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (task?.asset_id) {
      assetsService
        .getAssetById(task.asset_id)
        .then(setLinkedAsset)
        .catch(() => setLinkedAsset(null));
    } else {
      setLinkedAsset(null);
    }
  }, [task?.asset_id]);

  const loadTaskDetail = useCallback(async () => {
    if (!taskId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await tasksService.getTaskDetail(taskId);
      setTask(data);
    } catch (err) {
      console.error('Failed to load task:', err);
      setError('Failed to load action details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (isOpen && taskId) {
      setTask(null);
      setLinkedAsset(null);
      loadTaskDetail();
    }
  }, [isOpen, taskId, loadTaskDetail]);

  useEffect(() => {
    let cancelled = false;
    setUsers([]);
    if (isOpen && task?.facility_id) {
      facilitiesService.getAssignableUsers(task.facility_id)
        .then(data => { if (!cancelled) setUsers(data); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [isOpen, task?.facility_id]);

  const handleAddComment = async () => {
    if (!task || !comment.trim()) return;

    try {
      setSubmitting(true);
      const actingUser = currentUser || { id: task.created_by_id, name: 'User' };

      await tasksService.addComment(task.id, {
        user_id: actingUser.id,
        user_name: actingUser.name,
        comment: comment.trim()
      });

      showSuccess('Comment added successfully');
      setComment('');
      setShowCommentBox(false);
      await loadTaskDetail();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to add comment:', err);
      showError('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = async () => {
    if (!task || !selectedUserId) return;

    try {
      setSubmitting(true);
      const actingUser = currentUser || { id: task.created_by_id, name: 'User' };
      const newUser = users.find(u => u.id === selectedUserId);

      if (!newUser) {
        showError('Selected user not found');
        return;
      }

      await tasksService.reassignTask(task.id, {
        user_id: actingUser.id,
        user_name: actingUser.name,
        new_responsible_id: newUser.id,
        new_responsible_name: newUser.name
      });

      showSuccess('Task reassigned successfully');
      setShowReassignBox(false);
      setSelectedUserId('');
      await loadTaskDetail();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to reassign task:', err);
      showError('Failed to reassign task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeStatus = async () => {
    if (!task || !selectedStatus || (selectedStatus === 'blocked' && !statusComment.trim())) return;

    try {
      setSubmitting(true);

      await tasksService.changeStatus(task.id, {
        new_status: selectedStatus,
        ...(statusComment.trim() ? { comment: statusComment.trim() } : {})
      });

      showSuccess('Status updated successfully');
      setShowStatusChange(false);
      setSelectedStatus('');
      setStatusComment('');
      await loadTaskDetail();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to change status:', err);
      showError('Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!task) return;

    try {
      setSubmitting(true);
      await tasksService.completeTask(task.id);
      showSuccess('Task marked as complete');
      await loadTaskDetail();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to complete task:', err);
      showError(err instanceof Error ? err.message : 'Failed to mark task as complete');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRiskConfirm = async (likelihood: number, significance: number) => {
    if (!task?.asset_id) return;
    try {
      setSubmitting(true);
      const updated = await assetsService.updateAsset(task.asset_id, {
        risk_likelihood: likelihood,
        risk_significance: significance,
      });
      setLinkedAsset(updated);
      setShowRiskMatrixModal(false);
      showSuccess('Asset risk updated');
      onUpdated?.();
    } catch (err) {
      console.error('Failed to update asset risk:', err);
      showError('Failed to update risk');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePriorityRiskConfirm = async (likelihood: number, significance: number) => {
    if (!task) return;
    try {
      setSubmitting(true);
      const level = getRiskLevel(likelihood, significance);
      if (!level) throw new Error('Invalid risk matrix coordinates');
      await tasksService.updateTask(task.id, { risk_likelihood: likelihood, risk_consequence: significance, priority: task.priority });
      setShowPriorityRiskModal(false);
      showSuccess('Action risk updated');
      await loadTaskDetail();
      onUpdated?.();
    } catch (err) {
      console.error('Failed to update task risk:', err);
      showError('Failed to update risk');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !taskId) return null;

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { class: string; label: string; icon: any }> = {
      not_started: { class: 'status-badge not-started', label: 'Not Started', icon: Clock },
      acknowledged: { class: 'status-badge acknowledged', label: 'Acknowledged', icon: CheckCircle },
      started: { class: 'status-badge started', label: 'In Progress', icon: Clock },
      blocked: { class: 'status-badge blocked', label: 'Blocked', icon: AlertTriangle },
      complete: { class: 'status-badge complete', label: 'Complete', icon: CheckCircle },
      cancelled: { class: 'status-badge cancelled', label: 'Cancelled', icon: X }
    };
    return badges[status] || badges.not_started;
  };

  const isOverdue = task && task.due_by && new Date(task.due_by) < new Date() && task.status !== 'complete';

  const getActionTypeIcon = (actionType: string) => {
    switch (actionType) {
      case 'created': return <CheckCircle size={16} />;
      case 'status_changed': return <Edit3 size={16} />;
      case 'reassigned': return <UserCheck size={16} />;
      case 'comment': return <MessageSquare size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      default: return <FileText size={16} />;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content action-detail-modal">
        {/* Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <div className="action-icon-large">
              <Flag size={28} />
            </div>
            <div className="header-title-content">
              {loading ? (
                <h2>Loading...</h2>
              ) : task ? (
                <>
                  <WorkflowBreadcrumb task={task} onOpen={setNestedTaskId} />
                  <h2>{task.description}</h2>

                  {task.workflow_breadcrumb && <p>{task.workflow_breadcrumb.description}</p>}
                  <div className="header-badges">
                    {task.workflow && <a className="btn btn-secondary btn-sm" href={`${window.location.pathname.startsWith('/portal') ? '/portal' : ''}/lab-reports/${task.workflow.lab_report_id}`}>Open lab report <ExternalLink size={14} /></a>}
                    {(() => {
                      const statusBadge = getStatusBadge(task.status);
                      const StatusIcon = statusBadge.icon;

                      return (
                        <>
                          <label>Priority{' '}
                            <select className="form-select" aria-label="Action priority" value={task.priority} disabled={submitting}
                              onChange={async e => {
                                setSubmitting(true);
                                try { await tasksService.updateTask(task.id, { priority: e.target.value }); await loadTaskDetail(); onUpdated?.(); }
                                catch { showError('Failed to update priority'); }
                                finally { setSubmitting(false); }
                              }}>
                              <option value="low">{priorityLabel('low')}</option><option value="medium">{priorityLabel('medium')}</option>
                              <option value="high">{priorityLabel('high')}</option><option value="critical">{priorityLabel('critical')}</option>
                            </select>
                          </label>
                          <span className={statusBadge.class}>
                            <StatusIcon size={14} />
                            {statusLabel(task.status)}
                          </span>
                          {task.category && (
                            <span className="category-badge">
                              <Tag size={14} />
                              {task.category === 'lab_incident' ? 'Lab incident' : task.category}
                            </span>
                          )}
                          {isOverdue && (
                            <span className="status-badge overdue">
                              <AlertCircle size={14} />
                              Overdue
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <h2>Action Details</h2>
              )}
            </div>
          </div>
          <button className="close-button" aria-label="Close action details" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body action-detail-body">
          {error && (
            <div className="error-banner">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {loading || (isOpen && taskId && !task && !error) ? (
            <div className="loading-state">
              <Clock size={48} className="loading-spinner" />
              <p>Loading action details...</p>
            </div>
          ) : task ? (
            <>
              {task.workflow && <WorkflowSteps canMarkRetest={!!currentUser && ['consultant', 'h2okay_admin'].includes(currentUser.role)} onRetestMarked={async () => { await loadTaskDetail(); onUpdated?.(); }} variant="stepper" workflow={task.workflow} currentUserId={currentUser?.id} onOpen={setNestedTaskId} onStatusChange={currentUser && ['client', 'facility_manager'].includes(currentUser.role) ? async (id, status) => { await tasksService.changeStatus(id, {new_status: status}); await loadTaskDetail(); onUpdated?.(); } : undefined} onComplete={async id => { await tasksService.completeTask(id); await loadTaskDetail(); onUpdated?.(); }} />}
              {/* Context Section */}
              <div className="details-section context-section">
                <div className="section-header">
                  <h3>Context</h3>
                </div>
                <div className="context-grid">
                  {task.client_name && (
                    <div className="context-item">
                      <Building size={16} />
                      <div>
                        <label>Client</label>
                        <span>{task.client_name}</span>
                      </div>
                    </div>
                  )}
                  {task.facility_name && (
                    <div className="context-item">
                      <Building size={16} />
                      <div>
                        <label>Facility</label>
                        <span>{task.facility_name}</span>
                      </div>
                    </div>
                  )}
                  {task.asset_name && (
                    <div className="context-item">
                      <Package size={16} />
                      <div>
                        <label>Asset</label>
                        <button
                          type="button"
                          className="clickable"
                          onClick={() => {
                            if (task.facility_id) {
                              navigate(`/facilities/${task.facility_id}?tab=assets`);
                              onClose();
                            }
                          }}
                        >
                          {task.asset_name}
                          <ExternalLink size={12} />
                        </button>
                      </div>
                    </div>
                  )}
                  {task.source_inspection_id && (
                    <div className="context-item">
                      <FileText size={16} />
                      <div>
                        <label>Source Inspection</label>
                        <button
                          type="button"
                          className="clickable"
                          onClick={() => {
                            navigate(`/inspections/${task.source_inspection_id}`);
                            onClose();
                          }}
                        >
                          View Inspection
                          <ExternalLink size={12} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <section className="details-section"><h3>Action risk</h3>
                <RiskBadge likelihood={task.risk_likelihood} consequence={task.risk_consequence} />
                <button type="button" onClick={() => setShowPriorityRiskModal(true)}>Edit action risk</button>
              </section>
              {/* Risk (linked asset) - show when task has an asset */}
              {task.asset_id && (
                <div className="details-section risk-section">
                  <div className="section-header">
                    <h3>Asset risk</h3>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowRiskMatrixModal(true)}
                      disabled={submitting}
                      title="Edit risk level for linked asset"
                    >
                      <Edit3 size={14} />
                      Edit risk
                    </button>
                  </div>
                  <div className="risk-display-inline">
                    {linkedAsset ? (
                      getAssetOwnRisk(linkedAsset) ? (
                        <span className={`risk-badge risk-${toFacilityBadgeKey(getAssetOwnRisk(linkedAsset)!.level)}`}>
                          {riskLabel(getAssetOwnRisk(linkedAsset)!.level)} ({cellCode(getAssetOwnRisk(linkedAsset)!.likelihood, getAssetOwnRisk(linkedAsset)!.significance)})
                        </span>
                      ) : (
                        <p className="risk-empty-text">
                          <AlertTriangle size={14} />
                          No risk assessment for this asset. Click &quot;Edit risk&quot; to set.
                        </p>
                      )
                    ) : (
                      <p className="risk-empty-text">Loading asset…</p>
                    )}
                  </div>
                </div>
              )}

              {/* Assignment Section */}
              <div className="details-section assignment-section">
                <div className="section-header">
                  <h3>Assignment</h3>
                </div>
                <div className="assignment-grid">
                  <div className="assignment-item">
                    <UserIcon size={16} />
                    <div>
                      <label>Assigned To</label>
                      <span className="responsible-name">{task.responsible_user_name || 'Unknown'}</span>
                    </div>
                  </div>
                  <div className="assignment-item">
                    <UserIcon size={16} />
                    <div>
                      <label>Created By</label>
                      <span>{task.created_by_name || 'Unknown'}</span>
                    </div>
                  </div>
                  <div className="assignment-item">
                    <Calendar size={16} />
                    <div>
                      <label>Due Date</label>
                      <span className={isOverdue ? 'overdue-text' : ''}>
                        {formatShortDate(task.due_by)}
                      </span>
                    </div>
                  </div>
                  <div className="assignment-item">
                    <Calendar size={16} />
                    <div>
                      <label>Created</label>
                      <span>{formatShortDate(task.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subtasks */}
              {task.subtasks && task.subtasks.length > 0 && (
                <div className="details-section subtasks-section">
                  <div className="section-header">
                    <h3>Subtasks</h3>
                  </div>
                  <ul className="subtasks-list">
                    {task.subtasks.map((subtask, idx) => (
                      <li key={idx}>
                        <CheckCircle size={16} />
                        {subtask}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* History Timeline */}
              <div className="details-section history-section">
                <div className="section-header">
                  <h3>Activity History</h3>
                </div>
                <div className="history-timeline">
                  {task.action_history && task.action_history.length > 0 ? (
                    task.action_history.map((entry, idx) => {
                      const e = entry as typeof entry & { action_type?: string; details?: string; user_name?: string; previous_value?: string; new_value?: string };
                      return (
                        <div key={idx} className={`history-entry type-${(e.action_type ?? '').toLowerCase()}`}>
                          <div className="history-icon">
                            {getActionTypeIcon(e.action_type ?? '')}
                          </div>
                          <div className="history-content">
                            <div className="history-header">
                              <span className="history-user">{e.user_name ?? 'User'}</span>
                              <span className="history-time">{formatShortDate(entry.timestamp)}</span>
                            </div>
                            <div className="history-details">{formatTaskHistory(e.details ?? entry.description)}</div>
                            {e.previous_value && e.new_value && (
                              <div className="history-changes">
                                <span className="change-from">{formatTaskHistory(e.previous_value)}</span>
                                →
                                <span className="change-to">{formatTaskHistory(e.new_value)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="empty-history">No activity history yet.</p>
                  )}
                </div>
              </div>

              {/* Actions Panel */}
              <div className="details-section actions-panel">
                <div className="section-header">
                  <h3>Actions</h3>
                </div>

                <div className="action-buttons">
                  {task.status !== 'complete' && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowCommentBox(!showCommentBox)}
                        disabled={submitting}
                      >
                        <MessageSquare size={16} />
                        Add Comment
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowStatusChange(!showStatusChange)}
                        disabled={submitting || !!task.workflow || (task.workflow_instance_id ? !task.allowed_transitions?.length : false)}
                      >
                        <Edit3 size={16} />
                        Change Status
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowReassignBox(!showReassignBox)}
                        disabled={submitting}
                      >
                        <UserCheck size={16} />
                        Reassign
                      </button>

                      {task.workflow_breadcrumb?.transition === 'link_retest' && task.source_incident_id ?
                        <MarkRetest incidentId={task.source_incident_id} onMarked={async () => { await loadTaskDetail(); onUpdated?.(); }} /> : <>
                      <button
                        className="btn btn-primary"
                        onClick={handleMarkComplete}
                        title={task.workflow ? 'Close the incident by finishing its steps.' : task.workflow_breadcrumb?.blocked_reason || (task.workflow_breadcrumb?.transition === 'link_retest' ? RETEST_HINT : undefined)}
                        disabled={submitting || !!task.workflow || !!task.workflow_breadcrumb?.blocked_reason || task.workflow_breadcrumb?.transition === 'link_retest' || (task.workflow_instance_id ? !task.allowed_transitions?.includes('complete') : false)}
                      >
                        <CheckCircle size={16} />
                        {task.workflow_breadcrumb?.transition === 'acknowledge' ? 'Acknowledge' : task.workflow_breadcrumb?.transition === 'order_retest' ? 'Order re-test' : task.workflow_breadcrumb?.transition === 'close' ? 'Close incident' : 'Mark Complete'}
                      </button>
                      {(task.workflow || task.workflow_breadcrumb?.blocked_reason || task.workflow_breadcrumb?.transition === 'link_retest') && <small className="blocked-reason">{task.workflow ? 'Close the incident by finishing its steps.' : task.workflow_breadcrumb?.blocked_reason || RETEST_HINT}</small>}</>}

                    </>
                  )}
                </div>

                {/* Comment Box */}
                {showCommentBox && (
                  <div className="action-form">
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment..."
                      rows={4}
                      className="comment-textarea"
                    />
                    <div className="form-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          setShowCommentBox(false);
                          setComment('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddComment}
                        disabled={submitting || !comment.trim()}
                      >
                        {submitting ? 'Adding...' : 'Add Comment'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Status Change Box */}
                {showStatusChange && (
                  <div className="action-form">
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="status-select"
                    >
                      <option value="">Select new status...</option>
                      <option value="not_started">Not Started</option>
                      <option value="acknowledged">Acknowledged</option>
                      <option value="started">In Progress</option>
                      <option value="blocked">Blocked</option>
                      <option value="complete">Complete</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    {selectedStatus === 'blocked' && (
                      <textarea
                        value={statusComment}
                        onChange={(e) => setStatusComment(e.target.value)}
                        placeholder="Why is this blocked?"
                        rows={3}
                        className="comment-textarea"
                      />
                    )}
                    <div className="form-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          setShowStatusChange(false);
                          setSelectedStatus('');
                          setStatusComment('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleChangeStatus}
                        disabled={submitting || !selectedStatus || (selectedStatus === 'blocked' && !statusComment.trim())}
                      >
                        {submitting ? 'Updating...' : 'Update Status'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Reassign Box */}
                {showReassignBox && (
                  <div className="action-form">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="user-select"
                    >
                      <option value="">Select user...</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {assigneeLabel(user)}
                        </option>
                      ))}
                    </select>
                    <div className="form-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          setShowReassignBox(false);
                          setSelectedUserId('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleReassign}
                        disabled={submitting || !selectedUserId}
                      >
                        {submitting ? 'Reassigning...' : 'Reassign Task'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="error-state">
              <AlertCircle size={48} />
              <p>Failed to load action details</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="footer-info">
            {task && <span className="task-id">Task ID: {task.id.slice(0, 8)}...</span>}
          </div>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {task?.asset_id && (
        <RiskMatrixModal
          isOpen={showRiskMatrixModal}
          onClose={() => setShowRiskMatrixModal(false)}
          initialLikelihood={linkedAsset?.risk_likelihood}
          initialSignificance={linkedAsset?.risk_significance}
          onConfirm={handleRiskConfirm}
        />
      )}

      {task && (
        <RiskMatrixModal
          isOpen={showPriorityRiskModal}
          onClose={() => setShowPriorityRiskModal(false)}
          initialLikelihood={task.risk_likelihood ?? undefined}
          initialSignificance={task.risk_consequence ?? undefined}
          onConfirm={handlePriorityRiskConfirm}
        />
      )}
      {nestedTaskId && <ActionDetailModal isOpen taskId={nestedTaskId} onClose={() => { setNestedTaskId(null); loadTaskDetail(); }} onUpdated={() => { loadTaskDetail(); onUpdated?.(); }} />}
    </div>
  );
};

export default ActionDetailModal;
